import React from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Calendar, Clock, ArrowLeft, Share2, Facebook, Twitter, Linkedin, User, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import { ArticleSchema, BreadcrumbSchema, FAQSchema, HowToSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { blogPosts, businessInfo } from '../data/mock';
import { blogContent } from '../data/blogContent';

// Speakable schema for AI / voice answer engines (AEO)
const SpeakableSchema = ({ url, name }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    url,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.blog-quick-answer', 'h1'],
    },
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const content = blogContent[slug];

  if (!post) {
    return (
      <>
        <Seo
          title="Post Not Found | Basin Plumbing Blog"
          description="The blog post you're looking for doesn't exist. Browse our plumbing guides for Odessa & Midland, TX."
          path="/blog"
          noindex
        />
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-[#1a3a5c] mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
            <Link to="/blog">
              <Button data-testid="blog-not-found-back-button">Back to Blog</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const postUrl = `https://basinplumbing.com/blog/${post.slug}`;
  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Blog', url: 'https://basinplumbing.com/blog/' },
    { name: post.title, url: `${postUrl}/` }
  ];

  return (
    <>
      <Seo
        title={content?.metaTitle || post.title}
        description={content?.metaDescription || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image}
        publishedTime={post.date}
        modifiedTime={post.date}
      />
      <ArticleSchema article={{ ...post, keywords: content?.keywords }} />
      <BreadcrumbSchema items={breadcrumbs} />
      {content?.faqs && <FAQSchema customFaqs={content.faqs} />}
      {content?.howTo && (
        <HowToSchema
          name={content.howTo.name}
          description={content.howTo.description}
          steps={content.howTo.steps}
          image={post.image}
        />
      )}
      <SpeakableSchema url={postUrl} name={post.title} />

      <Header />

      <main className="main-content">
        {/* Hero Section */}
        <section className="pt-32 pb-8 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438]">
          <div className="max-w-4xl mx-auto px-4">
            <nav className="text-sm text-blue-200 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <span className="mx-2">/</span>
              <span className="text-white line-clamp-1">{post.title}</span>
            </nav>

            <span className="inline-block bg-[#e85a24] text-white px-3 py-1 rounded-full text-sm font-medium mb-4" data-testid="blog-post-category">
              {post.category}
            </span>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="blog-post-title">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-blue-200">
              <span className="flex items-center gap-2">
                <User size={16} />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={16} />
                {post.readTime}
              </span>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 -mt-4 relative z-10">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-xl"
          />
        </div>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid lg:grid-cols-[1fr_300px] gap-12">
              {/* Main Content */}
              <div>
                {/* AEO Quick Answer block */}
                {content?.quickAnswer && (
                  <div
                    className="blog-quick-answer mb-10 rounded-2xl border-l-4 border-[#e85a24] bg-[#fff5ef] p-6"
                    data-testid="blog-quick-answer"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={18} className="text-[#e85a24]" />
                      <h2 className="text-base font-bold uppercase tracking-wide text-[#e85a24] m-0">Quick Answer</h2>
                    </div>
                    <p className="text-gray-800 leading-relaxed m-0">{content.quickAnswer}</p>
                  </div>
                )}

                <article
                  className="prose prose-lg max-w-none prose-headings:text-[#1a3a5c] prose-a:text-[#e85a24] prose-a:no-underline hover:prose-a:underline prose-strong:text-[#1a3a5c] prose-table:text-sm"
                  data-testid="blog-post-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content?.content || '<p>Content coming soon...</p>') }}
                />
              </div>

              {/* Sidebar */}
              <aside className="space-y-6">
                {/* Share */}
                <Card className="border-0 shadow-lg sticky top-32">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
                      <Share2 size={18} />
                      Share This Article
                    </h3>
                    <div className="flex gap-3">
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${postUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors" data-testid="blog-share-facebook">
                        <Facebook size={18} />
                      </a>
                      <a href={`https://twitter.com/intent/tweet?url=${postUrl}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter" className="w-10 h-10 bg-sky-500 text-white rounded-lg flex items-center justify-center hover:bg-sky-600 transition-colors" data-testid="blog-share-twitter">
                        <Twitter size={18} />
                      </a>
                      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${postUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="w-10 h-10 bg-blue-700 text-white rounded-lg flex items-center justify-center hover:bg-blue-800 transition-colors" data-testid="blog-share-linkedin">
                        <Linkedin size={18} />
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Related Posts */}
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-[#1a3a5c] mb-4">Related Articles</h3>
                    <div className="space-y-4">
                      {blogPosts.filter(p => p.slug !== slug).slice(0, 3).map((relatedPost) => (
                        <Link
                          key={relatedPost.id}
                          to={`/blog/${relatedPost.slug}`}
                          className="block group"
                          data-testid={`blog-related-${relatedPost.slug}`}
                        >
                          <p className="text-sm font-medium text-[#1a3a5c] group-hover:text-[#e85a24] transition-colors line-clamp-2">
                            {relatedPost.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{relatedPost.readTime}</p>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* CTA Card */}
                <Card className="border-0 shadow-lg bg-[#1a3a5c] text-white">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">Need Help?</h3>
                    <p className="text-blue-100 text-sm mb-4">Contact our expert team for all your plumbing needs.</p>
                    <a href={`tel:${businessInfo.phone}`}>
                      <Button className="w-full bg-[#e85a24] hover:bg-[#d14a14] text-white" data-testid="blog-sidebar-call-button">
                        {businessInfo.phone}
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </aside>
            </div>

            {/* Back to Blog */}
            <div className="mt-12 pt-8 border-t">
              <Link to="/blog">
                <Button variant="outline" className="border-[#1a3a5c] text-[#1a3a5c]" data-testid="blog-back-button">
                  <ArrowLeft className="mr-2" size={18} />
                  Back to Blog
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection />
      </main>

      <Footer />
    </>
  );
};

export default BlogPostPage;
